import update from 'immutability-helper'
import Inflection from 'inflection'
import { debounce } from 'lodash'
import * as React from 'react'
import { FormFor } from 'react-rails-form-helpers'
import railProductCollage from 'images/igniter/checkout/billing_rail_products.png'
import {
  AbstractMembershipCheckoutForm,
  buildBaseCheckoutState,
  IBaseCheckoutProps,
  IBaseCheckoutState,
  resolveDefaultPurchaseOption,
} from '../AbstractMembershipCheckoutForm'
import { AddressFormFields, hasRequiredAddressFields } from '../Address/AddressForm'
import * as API from '../lib/api'
import { Checkout } from '../lib/checkout'
import { PurchaseOption } from '../lib/purchase_option'
import { TaxInformation } from '../lib/tax_information'
import { accountFieldName, Step1Account } from './Step1Account'
import {
  billingAddressFieldName,
  BillingCustomerField,
  billingCustomerFieldName,
  Step2Billing,
} from './Step2Billing'
import { RightRail } from './RightRail'
import {
  IStepContext,
  trackStepAdvance,
  trackStepView,
  TRIAL_REDESIGN_EXPERIMENT,
} from './stepAnalytics'
import {
  buildDraft,
  clearDraft,
  draftStorageKey,
  ICheckoutDraft,
  readDraft,
  writeDraft,
} from './lib/checkout_draft'
import { Step3Payment } from './Step3Payment'
import { StepAccordion } from './StepAccordion'
import { Timeline } from './Timeline'

declare var Routes: any
declare var $: any

// Must stay in lock-step with the Ruby `RenewalDiscount::DESCRIPTION` constant:
// the serialized `paymentDetails.adjustments` carry the discount under this
// label. A back-end rename silently zeroes the renewal copy;
// `spec/lib/renewal_discount_spec.rb` pins the constant to this literal.
const RENEWAL_DISCOUNT_LABEL = 'Loyalty Discount for Current Members'

const RAIL_PRODUCT_COLLAGE_ALT =
  'Igniter worship media: a Pray motion graphic, a Welcome slide, and a social media graphic'

export interface IStepDefinition {
  id: string
  label: string
}

export const STEPS: IStepDefinition[] = [
  { id: 'account', label: 'Create Your Account' },
  { id: 'billing', label: 'Billing Information' },
  { id: 'payment', label: 'Payment Information' },
]

export const CASHIER_FORM_NAME = 'cashier'

export interface IProps extends IBaseCheckoutProps {}

export interface IState extends IBaseCheckoutState {
  currentStepId: string
  completedStepIds: string[]
  // Held outside the serialized checkout/cashier object so it survives the step
  // unmounting (CD-167) without leaking into analytics/error payloads.
  password: string
  // Client-side comprehension gate, never posted; hoisted so it survives the
  // payment body unmounting on step navigation.
  chargeAcknowledged: boolean
  termsAccepted: boolean
}

export function entryStepId(checkout: Checkout): string {
  if (!checkout.signedIn) {
    return 'account'
  }
  if (!checkout.cashier.default_billing_address) {
    return 'billing'
  }
  return 'payment'
}

// `promo_code` is excluded: it comes from the apply-promo AJAX round-trip and
// renders inline at the promo field, so it must not read as a submit failure.
export function serverCheckoutFailed(errors: any): boolean {
  if (!errors) {
    return false
  }
  return Object.keys(errors).some((field) => field !== 'promo_code')
}

function applyDraftToCheckout(checkout: Checkout, draft: ICheckoutDraft): Checkout {
  return update(checkout, {
    cashier: {
      customer: { $merge: draft.customer || {} },
      billing_address: { $merge: draft.billingAddress || {} },
    },
  })
}

function hasCompleteBillingDetails(checkout: Checkout): boolean {
  const { customer, billing_address: address } = checkout.cashier

  return Boolean(customer.first_name && customer.last_name) && hasRequiredAddressFields(address)
}

// `selectedPurchaseOption()` throws on an unknown name, so a stored name that no
// longer exists must fall back rather than take the page down.
function restoredPurchaseOptionName(
  draft: ICheckoutDraft | null,
  base: IBaseCheckoutState,
): API.PurchaseOptionNameType {
  const stored = draft?.selectedPurchaseOptionName
  if (stored && base.checkout.purchaseOptions.some((o) => o.name === stored)) {
    return stored
  }
  return base.selectedPurchaseOptionName
}

// `password` is never persisted, so a signed-out visitor's account step can
// never be satisfied by a draft alone.
function furthestRestorableStepIndex(checkout: Checkout): number {
  if (!checkout.signedIn) {
    return 0
  }
  if (!hasCompleteBillingDetails(checkout)) {
    return 1
  }
  return STEPS.length - 1
}

// A stored step id is user-editable, so it is treated as a hint and clamped to
// what the restored values actually support.
function restoredStepId(draft: ICheckoutDraft | null, checkout: Checkout): string {
  const stored = STEPS.findIndex((s) => s.id === draft?.currentStepId)
  if (stored < 0) {
    return entryStepId(checkout)
  }
  return STEPS[Math.min(stored, furthestRestorableStepIndex(checkout))].id
}

// Recomputed rather than read from the draft: a stored array could mark billing
// complete with an empty address. `currentStepId` is already clamped to what the
// values support, so every step preceding it is satisfied.
function completedStepIdsFor(currentStepId: string): string[] {
  const index = STEPS.findIndex((s) => s.id === currentStepId)
  return STEPS.slice(0, index < 0 ? 0 : index).map((s) => s.id)
}

export class TrialRedesignForm extends AbstractMembershipCheckoutForm<IProps, IState> {
  private currentBodyRef = React.createRef<HTMLDivElement>()
  private draftKey: string
  private lastSerializedDraft = ''
  // `form.submit()` navigates but leaves the page alive, so a state update can
  // still land afterwards; the draft must stay cleared once submit has run.
  private submitted = false
  // `componentDidMount` alone drives a setState cascade (base-class
  // billing-address defaulting, then `updateTaxInformation`), so without a latch
  // a signed-in visitor who loads checkout and touches nothing still has their
  // name, email and billing address written to sessionStorage.
  private draftArmed = false

  // Diff-gated: a tax recalculation cycles `taxInformation` through its
  // placeholder and back, re-rendering twice with identical persisted fields.
  private persistDraft = debounce(() => {
    if (this.submitted || !this.draftArmed) {
      return
    }

    const draft = buildDraft(this.state)
    const serialized = JSON.stringify(draft)
    if (serialized === this.lastSerializedDraft) {
      return
    }

    this.lastSerializedDraft = serialized
    writeDraft(this.draftKey, draft)
  }, 300, { maxWait: 1000 })

  // Hydration is constructor-side because the billing inputs are uncontrolled
  // (`defaultValue` in AddressForm/Step2Billing): a post-mount `setState` would
  // never reach the DOM.
  constructor(props: IProps) {
    super(props)

    const base = buildBaseCheckoutState(props)
    const { code: planCode } = resolveDefaultPurchaseOption(base.checkout).membershipPlan
    this.draftKey = draftStorageKey(planCode, base.checkout.trial)

    const draft = readDraft(this.draftKey)
    const checkout = draft ? applyDraftToCheckout(base.checkout, draft) : base.checkout

    // A failed checkout re-renders inline at the payment step, not the entry
    // step, so the user lands on the retry affordance with the error in view.
    const currentStepId = serverCheckoutFailed(base.checkout.cashier.errors)
      ? 'payment'
      : restoredStepId(draft, checkout)

    this.state = {
      ...base,
      checkout,
      selectedPurchaseOptionName: restoredPurchaseOptionName(draft, base),
      currentStepId,
      completedStepIds: completedStepIdsFor(currentStepId),
      password: '',
      chargeAcknowledged: false,
      termsAccepted:
        typeof draft?.termsAccepted === 'boolean'
          ? draft.termsAccepted
          : base.checkout.cashier.terms_of_service,
    }

    // A failed POST does `render :new` rather than redirecting
    // (Igniter::CheckingOutMembershipAdapter#failure), so the submitted values
    // arrive back in props against a draft that submit already cleared.
    if (serverCheckoutFailed(base.checkout.cashier.errors)) {
      writeDraft(this.draftKey, buildDraft(this.state))
    }
  }

  // Entry step varies by sign-in/billing state, so emit the funnel's first
  // `step_view` on mount rather than only on advance.
  public componentDidMount() {
    super.componentDidMount()
    this.trackStepView(this.state.currentStepId)
    window.addEventListener('pagehide', this.flushDraft)
  }

  public componentDidUpdate() {
    this.persistDraft()
  }

  // The cancel must precede the clear: the state change that opened the submit
  // schedules a write, and a 300ms timer surviving into `form.submit()` would
  // rewrite the draft microseconds after it was removed.
  protected beforeSubmit(): void {
    this.submitted = true
    this.persistDraft.cancel()
    clearDraft(this.draftKey)
  }

  // `pagehide` rather than `beforeunload`: it is bfcache-friendly and fires
  // reliably on mobile Safari. The form is mounted for the life of the page, so
  // a refresh or navigation only ever reaches this listener, never unmount.
  private flushDraft = () => {
    this.persistDraft.flush()
  }

  // `super` is required here: the base class unloads the Accept.js script tag.
  public componentWillUnmount() {
    super.componentWillUnmount()
    window.removeEventListener('pagehide', this.flushDraft)
    this.persistDraft.flush()
    // Fail the seq check for any tax request still in flight, so its resolution
    // does not `setState` on an unmounted component.
    this.taxRequestSeq = -1
  }

  public handleStepSelect = (stepId: string) => {
    if (!this.isCompleted(stepId)) {
      return
    }
    this.goToStep(stepId)
  }

  public goToStep = (stepId: string) => {
    this.draftArmed = true
    const { currentStepId, completedStepIds } = this.state
    const fromIndex = STEPS.findIndex((s) => s.id === currentStepId)
    const toIndex = STEPS.findIndex((s) => s.id === stepId)
    const advancing = toIndex > fromIndex
    const completed = completedStepIds.includes(currentStepId)
      ? completedStepIds
      : [...completedStepIds, currentStepId]

    // Focus in the setState callback: the target step body isn't mounted until
    // the state update commits.
    this.setState({ currentStepId: stepId, completedStepIds: completed }, () => {
      if (advancing) {
        this.focusFirstFieldOfStep()
      }
    })

    // First-reach signal: fire once when the visitor first moves forward into a
    // step, so back-then-forward bouncing can't inflate advance counts.
    if (advancing && !completedStepIds.includes(stepId)) {
      trackStepAdvance(this.stepContext(), fromIndex + 1, toIndex + 1)
    }
    this.trackStepView(stepId)
  }

  protected focusFirstFieldOfStep() {
    const body = this.currentBodyRef.current
    if (!body) {
      return
    }

    const field = body.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
    )
    if (!field) {
      return
    }

    // preventScroll keeps focus from yanking the viewport; then smooth-scroll
    // the field into view ourselves (guarded — jsdom has no scrollIntoView).
    field.focus({ preventScroll: true })
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  public accountEmailChanged = (value: string) => {
    this.draftArmed = true
    const { checkout: { cashier } } = this.state
    const updatedCashier = update(cashier, { customer: { email: { $set: value } } })
    this.updateCashier(updatedCashier)
  }

  public chargeAcknowledgedChanged = (value: boolean) => {
    this.setState({ chargeAcknowledged: value })
  }

  public termsAcceptedChanged = (value: boolean) => {
    this.draftArmed = true
    this.setState({ termsAccepted: value })
  }

  public accountPasswordChanged = (value: string) => {
    this.setState({ password: value })
  }

  public billingCustomerChanged = (field: BillingCustomerField, value: string) => {
    this.draftArmed = true
    const { checkout: { cashier } } = this.state
    const updatedCashier = update(cashier, { customer: { [field]: { $set: value } } })
    this.updateCashier(updatedCashier)
  }

  // `componentDidMount` calls the inherited `billingAddressChanged` directly, so
  // the latch is applied at the prop boundary rather than by overriding it —
  // only the address form's own edits arrive here.
  public billingAddressEdited = (field: keyof API.IAddress, value: string) => {
    this.draftArmed = true
    this.billingAddressChanged(field, value)
  }

  public billingAddressFieldsEdited = (fields: Partial<API.IAddress>) => {
    this.draftArmed = true
    this.billingAddressFieldsChanged(fields)
  }

  public purchaseOptionChanged = (name: API.PurchaseOptionNameType) => {
    this.draftArmed = true
    const { taxInformation } = this.state
    const { totals } = taxInformation

    if (totals && totals[name]) {
      this.setState({
        selectedPurchaseOptionName: name,
        taxInformation: TaxInformation.fromOrderTotals(totals, name),
      })
    } else {
      this.setState({ selectedPurchaseOptionName: name }, () => this.updateTaxInformation())
    }
  }

  // Deliberately NOT hoisted to the shared base (along with
  // `handlePromoCodeResponse`): this is the A/B variant seam, and pulling the
  // promo round-trip up would change the live control cell's code path. Hoist
  // if the experiment graduates and the seam is removed.
  public applyPromoCode = (e: React.SyntheticEvent) => {
    const button = $(e.target)
    const form = button.parents('form').first()
    const { checkout: { cashier } } = this.state

    const updatedCashier = update(cashier, { errors: { $unset: ['promo_code'] } })
    this.updateCashier(updatedCashier)

    $.post({
      url: Routes.igniter_membership_checkout_apply_promo_code_path(),
      data: form.serialize(),
      dataType: 'json',
    }).done(this.handlePromoCodeResponse)
  }

  public ctaText(): string {
    return this.state.checkout.trial ? 'Start Your Trial' : 'Start Your Membership'
  }

  public render() {
    const { checkout } = this.state
    const { trial } = checkout
    const planName = this.selectedPurchaseOption().membershipPlan.name

    return (
      <div
        className="checkout membership-checkout trial-redesign"
        data-testid="trial-redesign-form"
      >
        <div className="grid-x">
          <header className="cell trial-redesign__header">
            <h1 className="trial-redesign__title">
              <span className="trial-redesign__title-accent">Wingman</span> Request Form
            </h1>
            <p className="trial-redesign__subtitle" data-testid="trial-redesign-subtitle">
              Just kidding. You're about to sign up for {trial ? 'a free trial of the' : 'our'}{' '}
              <strong>{planName}</strong> plan.
            </p>
          </header>
        </div>

        {/* Intentionally NOT wrapped in `.grid-container`, whose max-width would
            cap and inset it: the accordion must span the full viewport so its
            section rules are full-bleed (see trial-accordion.scss). */}
        <FormFor url={Routes.igniter_membership_checkout_path()} method="post" name={CASHIER_FORM_NAME}>
          {this.renderSubmitErrors()}
          {this.renderAccordion()}
          {this.renderHiddenFields()}
        </FormFor>
      </div>
    )
  }

  protected renderAccordion() {
    const { currentStepId, completedStepIds } = this.state

    return (
      <StepAccordion
        steps={STEPS}
        currentStepId={currentStepId}
        completedStepIds={completedStepIds}
        onStepSelect={this.handleStepSelect}
        renderBody={(step) => this.renderStepContent(step)}
        currentBodyRef={this.currentBodyRef}
      />
    )
  }

  protected renderStepContent(step: IStepDefinition) {
    return (
      <>
        {step.id === 'payment' && this.state.checkout.trial && this.renderTimeline()}
        <div className={`trial-step__split trial-step__split--${step.id}`}>
          <div className="trial-step__fields">{this.renderStepBody(step)}</div>
          <aside
            className="trial-step__rail trial-redesign__rail"
            data-testid="trial-redesign-rail"
            data-current-step={step.id}
          >
            {this.renderRightRail(step.id)}
          </aside>
        </div>
      </>
    )
  }

  // Rendered here, not inside Step3Payment, so the band can break out of the
  // form column to the full-bleed width the Figma timeline requires.
  protected renderTimeline() {
    const { checkout, taxInformation } = this.state
    const { trial, trialLength } = checkout
    const option = this.selectedPurchaseOption()

    return (
      <div className="trial-step__timeline-band">
        <div className="trial-step__timeline-inner">
          <Timeline
            trial={trial}
            trialLength={trialLength}
            totalIncludedItemsInTrial={option.membershipPlan.totalIncludedItemsInTrial}
            planName={option.membershipPlan.name}
            includedItemsInTrial={option.membershipPlan.includedItemsInTrial}
            paymentDetails={option.paymentDetails}
            taxInformation={taxInformation}
            loyaltyRenewalDiscount={this.loyaltyRenewalDiscount(option)}
          />
        </div>
      </div>
    )
  }

  protected renderRightRail(stepId: string) {
    const { checkout, selectedPurchaseOptionName, taxInformation } = this.state

    return (
      <RightRail
        currentStepId={stepId}
        checkoutTestimonials={checkout.checkoutTestimonials}
        purchaseOptions={checkout.purchaseOptions}
        selectedPurchaseOptionName={selectedPurchaseOptionName}
        onPurchaseOptionChange={this.purchaseOptionChanged}
        taxInformation={taxInformation}
        totals={taxInformation.totals ?? undefined}
        trial={checkout.trial}
        productImagePath={railProductCollage}
        productImageAlt={RAIL_PRODUCT_COLLAGE_ALT}
      />
    )
  }

  // Collapsed-step persistence (CD-167): a step's body unmounts when it stops
  // being active, so field values are hoisted into the shell (email onto
  // `cashier.customer.email`, password into `state.password`) and handed back as
  // controlled props. `renderHiddenFields` mirrors them for final submit.
  protected renderStepBody(step: IStepDefinition) {
    if (step.id === 'account') {
      return (
        <Step1Account
          // A new customer serializes with a null email, but the input is
          // controlled and needs a string from the first render — a null flips
          // it uncontrolled→controlled on first keystroke (React warns twice).
          email={this.state.checkout.cashier.customer.email ?? ''}
          password={this.state.password}
          signedIn={this.state.checkout.signedIn}
          onEmailChange={this.accountEmailChanged}
          onPasswordChange={this.accountPasswordChanged}
          onContinue={() => this.goToStep('billing')}
        />
      )
    }

    if (step.id === 'billing') {
      const { checkout } = this.state
      const { cashier, stateSelectOptions, countrySelectOptions } = checkout

      return (
        <Step2Billing
          customer={cashier.customer}
          address={cashier.billing_address}
          stateSelectOptions={stateSelectOptions}
          countrySelectOptions={countrySelectOptions}
          onCustomerChanged={this.billingCustomerChanged}
          onAddressChanged={this.billingAddressEdited}
          onContinue={() => this.goToStep('payment')}
          mapbox={this.props.mapbox}
          onAddressFieldsChanged={this.billingAddressFieldsEdited}
        />
      )
    }

    if (step.id === 'payment') {
      const { checkout, taxInformation, paymentToken } = this.state
      const { cashier, trial, trialLength } = checkout
      const {
        payment_type: paymentType,
        payment_profiles: paymentProfiles,
        payment_profile_id: selectedPaymentProfileId,
        credit_card: creditCard,
        allow_promo_code: allowPromoCode,
        promo_code: promoCode,
        errors,
      } = cashier
      const option = this.selectedPurchaseOption()
      const { paymentDetails } = option

      return (
        <Step3Payment
          trial={trial}
          trialLength={trialLength}
          paymentDetails={paymentDetails}
          taxInformation={taxInformation}
          paymentProfiles={paymentProfiles}
          selectedPaymentProfileId={selectedPaymentProfileId}
          creditCard={creditCard}
          paymentType={paymentType}
          paymentToken={paymentToken}
          allowPromoCode={allowPromoCode}
          promoCode={promoCode}
          promoErrors={errors.promo_code || []}
          applyPromoCode={this.applyPromoCode}
          termsOfService={this.state.termsAccepted}
          chargeAcknowledged={this.state.chargeAcknowledged}
          onTermsAcceptedChange={this.termsAcceptedChanged}
          onChargeAcknowledgedChange={this.chargeAcknowledgedChanged}
          ctaText={this.ctaText()}
          onCashierSubmit={this.onCheckoutFormSubmit}
        />
      )
    }

    return null
  }

  protected handlePromoCodeResponse = (response: API.IPromoCodeResponse) => {
    const { checkout } = this.state
    const { cashier } = checkout
    const { checkout: checkoutResponse } = response
    const { purchase_options: purchaseOptions } = checkoutResponse
    const { promo_code, errors } = checkoutResponse.cashier

    const setErrors = errors.promo_code
      ? { promo_code: { $set: errors.promo_code } }
      : { $unset: ['promo_code'] }

    const newPurchaseOptions = purchaseOptions.map((o) => new PurchaseOption(o))

    const updatedCashier = update(cashier, {
      promo_code: { $set: promo_code },
      errors: setErrors,
    })

    this.setState({
      checkout: {
        ...checkout,
        cashier: updatedCashier,
        purchaseOptions: newPurchaseOptions,
      },
    })
  }

  // The forward-looking renewal discount isn't serialized on its own, so proxy
  // it from the renewal-discount adjustment in the current order's breakdown.
  // Absent (new members with no current membership) it is $0.
  protected loyaltyRenewalDiscount(option: PurchaseOption): number {
    const adjustment = option.paymentDetails.adjustments.find(
      (a) => a.label === RENEWAL_DISCOUNT_LABEL,
    )
    return adjustment ? Math.abs(adjustment.value) : 0
  }

  // Covers both rejection paths: server-side `cashier.errors` from a failed POST
  // (parity with the control form's `cashierErrors()`) and client-side Accept.js
  // tokenization failures. `promo_code` is omitted — it renders inline.
  protected renderSubmitErrors() {
    const { checkout, paymentTokenErrors } = this.state
    const cashierErrors = checkout.cashier.errors || {}

    // Rails serializes `base` as an array of messages, so flatten to one <li>
    // each; rendering the array directly mashes them into a run-on line.
    const serverErrors: React.ReactNode[] = []
    Object.keys(cashierErrors)
      .filter((field) => field !== 'promo_code')
      .forEach((field) => {
        const value = cashierErrors[field]
        const messages = Array.isArray(value) ? value : [value]
        const label = field === 'base' ? '' : `${Inflection.titleize(field)} `
        messages.forEach((message: string, i: number) => {
          serverErrors.push(
            <li key={`${field}-${i}`}>
              {label}
              {message}
            </li>,
          )
        })
      })

    const tokenErrors = (paymentTokenErrors || []).map(({ code, text }) => (
      <li key={code}>Credit/Debit Card: {text}</li>
    ))

    const errors = serverErrors.concat(tokenErrors)
    if (errors.length === 0) {
      return null
    }

    return (
      <div className="cashier-errors" data-testid="trial-redesign-payment-errors" role="alert">
        <h2>Please correct the errors below to continue:</h2>
        <ul>{errors}</ul>
      </div>
    )
  }

  protected renderHiddenFields() {
    const { checkout } = this.state
    const { trial } = checkout
    const selectedPurchaseOption = this.selectedPurchaseOption()
    const monthly: boolean = selectedPurchaseOption.name === 'monthly'
    const { code: planCode } = selectedPurchaseOption.membershipPlan

    return (
      <React.Fragment>
        {planCode ? <input type="hidden" name="plan_code" value={planCode} /> : null}
        <input type="hidden" name="monthly" value={monthly ? 1 : 0} />
        {trial ? <input type="hidden" name="trial" value={1} /> : null}
        {this.renderCollapsedAccountFields()}
        {this.renderCollapsedBillingFields()}
      </React.Fragment>
    )
  }

  // Mirror the hoisted email/password as hidden inputs while the account body is
  // unmounted, so final submit still posts them. Skipped when the step is active
  // (live inputs would duplicate these) or the visitor is signed in (the control
  // variant omits customer fields entirely, so posting them would diverge).
  protected renderCollapsedAccountFields() {
    const { currentStepId, password, checkout } = this.state

    if (currentStepId === 'account' || checkout.signedIn) {
      return null
    }

    const { email } = checkout.cashier.customer

    return (
      <React.Fragment>
        <input type="hidden" name={accountFieldName(CASHIER_FORM_NAME, 'email')} value={email} />
        <input type="hidden" name={accountFieldName(CASHIER_FORM_NAME, 'password')} value={password} />
      </React.Fragment>
    )
  }

  // Mirror the hoisted billing payload while the billing body is unmounted.
  // Skipped when the step is active (would duplicate live inputs), billing isn't
  // complete, or the visitor uses a stored default address (the control posts
  // `use_default_billing_address` instead, so mirroring would diverge).
  protected renderCollapsedBillingFields() {
    const { currentStepId, checkout } = this.state

    if (
      currentStepId === 'billing' ||
      !this.isCompleted('billing') ||
      !this.collectsBillingFromUser()
    ) {
      return null
    }

    const { customer, billing_address: address } = checkout.cashier
    const { first_name, last_name, company } = customer
    const { address1, address2, city, state, province, zip, country } = address
    const customerField = (field: BillingCustomerField) =>
      billingCustomerFieldName(CASHIER_FORM_NAME, field)
    const addressField = (field: AddressFormFields) =>
      billingAddressFieldName(CASHIER_FORM_NAME, field)

    // `billingAddressChanged` $sets only the changed field and never clears its
    // sibling, so a US-then-non-US switch leaves a stale `state`. Gate on country
    // exactly as the live `AddressForm` does, so the mirror doesn't post both.
    const isUSA = country === 'USA'

    return (
      <React.Fragment>
        <input type="hidden" name={customerField('first_name')} value={first_name} />
        <input type="hidden" name={customerField('last_name')} value={last_name} />
        <input type="hidden" name={customerField('company')} value={company} />
        <input type="hidden" name={addressField(AddressFormFields.Country)} value={country} />
        <input type="hidden" name={addressField(AddressFormFields.Address1)} value={address1} />
        <input type="hidden" name={addressField(AddressFormFields.Address2)} value={address2} />
        <input type="hidden" name={addressField(AddressFormFields.City)} value={city} />
        {isUSA && state !== undefined ? (
          <input type="hidden" name={addressField(AddressFormFields.State)} value={state} />
        ) : null}
        {!isUSA && province !== undefined ? (
          <input type="hidden" name={addressField(AddressFormFields.Province)} value={province} />
        ) : null}
        <input type="hidden" name={addressField(AddressFormFields.Zip)} value={zip} />
        {/* Match the live form's `<HiddenField value={false} />`, which
            serializes to "false"; `value={0}` was a needless divergence. */}
        <input type="hidden" name={`${CASHIER_FORM_NAME}[use_default_billing_address]`} value="false" />
      </React.Fragment>
    )
  }

  protected isCompleted(stepId: string): boolean {
    return this.state.completedStepIds.includes(stepId)
  }

  // Read the variant from the assignment map rather than assuming `redesign`;
  // tag a missing assignment `unknown` (not blank) so anomalous rows stay
  // filterable downstream.
  protected stepContext(): IStepContext {
    const { experimentVariants } = this.state.checkout
    return {
      experiment: TRIAL_REDESIGN_EXPERIMENT,
      variant: experimentVariants[TRIAL_REDESIGN_EXPERIMENT] || 'unknown',
    }
  }

  protected trackStepView(stepId: string) {
    const index = STEPS.findIndex((s) => s.id === stepId)
    if (index < 0) {
      return
    }
    trackStepView(this.stepContext(), index + 1, stepId)
  }

  // Mirrors the entry-point rule: a signed-in visitor with a stored default
  // billing address skips the billing step and uses that address instead.
  protected collectsBillingFromUser(): boolean {
    const { signedIn, cashier: { default_billing_address } } = this.state.checkout
    return !(signedIn && default_billing_address)
  }

  protected async updateTaxInformation() {
    const seq = ++this.taxRequestSeq
    const { selectedPurchaseOptionName, checkout } = this.state
    const { cashier, trial, purchaseOptions } = checkout
    const {
      billing_address: billingAddress,
      default_billing_address: defaultBillingAddress,
      use_default_billing_address: useDefaultBillingAddress,
    } = cashier
    const option = purchaseOptions.find((o) => o.name == selectedPurchaseOptionName)

    // Tax exemption is applied server-side (it honors the customer's exemption
    // certificates), so unlike the legacy path we don't short-circuit here.
    if (!option) {
      this.setState({ taxInformation: TaxInformation.no_tax } as Pick<IState, keyof IState>)
      return
    } else {
      this.setState({ taxInformation: TaxInformation.placeholder } as Pick<IState, keyof IState>)
    }

    const address = useDefaultBillingAddress ? defaultBillingAddress : billingAddress

    const taxInformation = await TaxInformation.calculateOrderTotals({
      address,
      planCode: option.membershipPlan.code,
      monthly: selectedPurchaseOptionName === 'monthly',
      trial,
    })

    if (seq !== this.taxRequestSeq) { return }

    this.setState({ taxInformation } as Pick<IState, keyof IState>)
  }
}
